/**
 * baseurlv2HttpRequest: HTTP request using baseurlv2 for special endpoints
 * Used for /trigger-otp and /validate-mfa endpoints
 */
export const baseurlv2HttpRequest = async (config) => {
    let baseURL = config.baseURL;
    if (!baseURL && typeof window !== "undefined") {
        const rawBaseUrlV2 = window.__authShellConfig?.baseurlv2;
        if (rawBaseUrlV2) {
            // Ensure unauth segment is present exactly once for v2 unauth endpoints.
            baseURL = /\/unauth\/?$/.test(rawBaseUrlV2)
                ? rawBaseUrlV2
                : `${rawBaseUrlV2.replace(/\/+$/, "")}/unauth`;
        }
    }
    // Use unauthHttpRequest logic and override baseURL only when available.
    return unauthHttpRequest({
        ...config,
        ...(baseURL ? { baseURL } : {}),
    });
};
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
/**
 * Shared promise for unAuth token refresh on 403 retry.
 * Ensures concurrent 403 retriers all await the same refresh and do not trigger multiple refresh calls.
 */
let unauthRefreshOn403Promise = null;
/**
 * Authorized HTTP request function for React Query
 * This is the mutator function referenced in orval.config.ts
 */
export const authHttpRequest = async (config) => {
    // Dynamic import to break circular dependency at build time
    // @ts-ignore - core-auth is available at runtime but not at build time
    const coreAuth = await import("@fmc-digital/core-auth");
    const source = axios.CancelToken.source();
    // @ts-ignore - authorizedHttp type not available at build time
    const promise = coreAuth.authorizedHttp({
        ...config,
        cancelToken: source.token,
    }).then(({ data }) => data);
    // Adding cancel method to the promise for React Query
    promise.cancel = () => {
        source.cancel("Query was cancelled by React Query");
    };
    return promise;
};
/** Buffer (seconds) before token expiry at which we refresh: clock skew + time to complete refresh (e.g. network + 30s timeout). */
const UNAUTH_TOKEN_REFRESH_BUFFER_SECONDS = 120;
/** Cooldown (ms) after a refresh that didn't update expiry — prevents infinite loop when refresh consistently fails. */
const UNAUTH_REFRESH_NO_UPDATE_COOLDOWN_MS = 60_000;
/** Timestamp when we last detected refresh did not update token expiry; 0 = not in cooldown. */
let lastUnauthRefreshNoUpdateTime = 0;
/**
 * Some unauth endpoints return HTTP 2xx with a JSON body that uses `errorDetails`
 * instead of a non-success status. Treat that as failure so callers get onError
 * and can surface messages via getErrorMessageFromApiError.
 * An empty `errorDetails` array is ignored (not treated as failure).
 */
function ensureUnauthResponseHasNoErrorEnvelope(response) {
    const data = response.data;
    const errorDetails = data && typeof data === "object" && "errorDetails" in data
        ? data.errorDetails
        : undefined;
    const hasMeaningfulErrorDetails = errorDetails != null &&
        !(Array.isArray(errorDetails) && errorDetails.length === 0);
    if (hasMeaningfulErrorDetails) {
        throw new axios.AxiosError("Request failed with error in response body", String(response.status), response.config, response.request, response);
    }
    if (response.data == null) {
        throw new axios.AxiosError("Response body was null or undefined", String(response.status), response.config, response.request, response);
    }
    return response.data;
}
/**
 * Unauthenticated HTTP request function for React Query
 * Used for endpoints that require unauthenticated Okta token (e.g., recover-username)
 * Gets the unAuthToken from localStorage or uses the Authorization header if provided
 * BaseURL should be configured in the app (e.g., via initPublicHttp or similar)
 *
 * @param config - Axios request config. Use config.baseURL to explicitly set a base URL,
 *                 or use config.headers['X-Use-BaseUrl'] = '1' to use apiBaseUrl1 instead of apiBaseUrl
 */
export const unauthHttpRequest = async (config) => {
    const source = axios.CancelToken.source();
    // Refresh expired unAuthToken before request (fixes intermittent 403 AccessDenied)
    if (typeof window !== "undefined" && !config.headers?.Authorization) {
        const authShellConfig = window.__authShellConfig;
        const getConfigValue = authShellConfig?.getConfigValue;
        const tokenExpiry = getConfigValue?.("timer");
        if (tokenExpiry && authShellConfig?.refreshUnauthTokenIfNeeded) {
            const currentTime = Math.floor(Date.now() / 1000);
            const expiryTime = parseInt(tokenExpiry, 10);
            const isValidExpiry = Number.isFinite(expiryTime);
            const needsRefresh = !isValidExpiry || expiryTime <= currentTime + UNAUTH_TOKEN_REFRESH_BUFFER_SECONDS;
            const inCooldown = Date.now() - lastUnauthRefreshNoUpdateTime < UNAUTH_REFRESH_NO_UPDATE_COOLDOWN_MS;
            if (needsRefresh && !inCooldown) {
                try {
                    await authShellConfig.refreshUnauthTokenIfNeeded();
                    // Verify refresh actually updated expiry to avoid infinite loop when refresh fails to update
                    const newExpiryRaw = getConfigValue?.("timer");
                    const newExpiry = newExpiryRaw != null ? parseInt(newExpiryRaw, 10) : NaN;
                    const stillExpiredOrNear = !Number.isFinite(newExpiry) || newExpiry <= currentTime + UNAUTH_TOKEN_REFRESH_BUFFER_SECONDS;
                    if (stillExpiredOrNear) {
                        lastUnauthRefreshNoUpdateTime = Date.now();
                    }
                }
                catch (refreshError) {
                    lastUnauthRefreshNoUpdateTime = Date.now();
                    // Proceed with request; token refresh failure (network/API) should not block the call
                    console.warn("[service.ts] unAuth token refresh failed, proceeding with request:", refreshError);
                }
            }
        }
    }
    // Check if caller wants to use baseUrlt1 (alternative base URL)
    const useBaseUrlt1 = config.headers?.['X-Use-BaseUrl'] === '1';
    // Get baseURL from config or window runtime config
    // The baseURL should be set when the app initializes
    let baseURL = config.baseURL;
    if (!baseURL && typeof window !== "undefined") {
        // Try to get baseURL from window runtime config (set by auth-shell App.tsx)
        // Format: apiBaseUrl should be like "https://gateway-dev.freedommortgage.com/api/v1/selfserv"
        // We append "/unauth" for unauth endpoints
        const runtimeConfig = window.__authShellConfig;
        const apiBaseUrl = useBaseUrlt1 ? runtimeConfig?.apiBaseUrlt1 : runtimeConfig?.apiBaseUrl;
        if (apiBaseUrl) {
            // Append /unauth if not already there (applies to both regular and T1 endpoints)
            if (!apiBaseUrl.includes("/unauth")) {
                baseURL = `${apiBaseUrl}/unauth`;
            }
            else {
                baseURL = apiBaseUrl;
            }
        }
    }
    // If still no baseURL, use the default from swagger (dev environment)
    // In production, this should be configured via app initialization
    if (!baseURL) {
        baseURL = "https://gateway-dev.freedommortgage.com/api/v1/selfserv/unauth";
    }
    // Get unAuthToken from config if Authorization header is not already set
    let unAuthToken = null;
    if (!config.headers?.Authorization) {
        // Try to get unAuthToken from config (stored by setUnAuthTokenAndTimer in auth-shell)
        if (typeof window !== "undefined") {
            const authShellConfig = window.__authShellConfig;
            if (authShellConfig?.getConfigValue) {
                unAuthToken = authShellConfig.getConfigValue("unAuthToken") || null;
            }
        }
        if (unAuthToken) {
            config.headers = {
                ...config.headers,
                Authorization: `Bearer ${unAuthToken}`,
            };
        }
        else {
            // Log warning if Authorization token is missing (critical for unauth endpoints)
            console.warn("[service.ts] unAuthToken not found. This may cause the API request to fail.", "URL:", config.url);
        }
    }
    // Generate correlation ID if not provided
    let correlationId = config.headers?.["x-correlation-id"];
    if (!correlationId) {
        correlationId = uuidv4();
    }
    // Get x-api-key from window runtime config if not provided
    let xApiKey = config.headers?.["x-api-key"];
    if (!xApiKey && typeof window !== "undefined") {
        const runtimeConfig = window.__authShellConfig;
        xApiKey = runtimeConfig?.xApiKey || undefined;
        // Log warning if x-api-key is missing (for debugging)
        if (!xApiKey) {
            console.warn("[service.ts] x-api-key not found in runtime config. Available keys:", Object.keys(runtimeConfig || {}));
        }
    }
    // Get captcha token and GUID from config (set by RecoverEmailForm/RecoverPasswordForm)
    // Supports both recover-username and recover-password flows
    let captchaToken;
    let guid;
    if (typeof window !== "undefined") {
        const authShellConfig = window.__authShellConfig;
        if (authShellConfig?.getConfigValue) {
            // Get captcha token for recover-username or recover-password request
            const recoverUsernameCaptchaToken = authShellConfig.getConfigValue("recoverUsernameCaptchaToken");
            const recoverPasswordCaptchaToken = authShellConfig.getConfigValue("recoverPasswordCaptchaToken");
            if (recoverUsernameCaptchaToken) {
                captchaToken = recoverUsernameCaptchaToken;
            }
            else if (recoverPasswordCaptchaToken) {
                captchaToken = recoverPasswordCaptchaToken;
            }
            // First check for request GUID (for recover-username or recover-password call)
            const recoverUsernameRequestGuid = authShellConfig.getConfigValue("recoverUsernameRequestGuid");
            const recoverPasswordRequestGuid = authShellConfig.getConfigValue("recoverPasswordRequestGuid");
            if (recoverUsernameRequestGuid) {
                guid = recoverUsernameRequestGuid;
            }
            else if (recoverPasswordRequestGuid) {
                guid = recoverPasswordRequestGuid;
            }
            else {
                // Check for stored GUID from recover-username or recover-password response (for subsequent API calls)
                // This GUID is used for trigger-mfa and validate-mfa
                const recoverUsernameGuid = authShellConfig.getConfigValue("recoverUsernameGuid");
                const recoverPasswordGuid = authShellConfig.getConfigValue("recoverPasswordGuid");
                if (recoverUsernameGuid) {
                    guid = recoverUsernameGuid;
                }
                else if (recoverPasswordGuid) {
                    guid = recoverPasswordGuid;
                }
            }
        }
    }
    // Generate GUID if not provided (only for recover-username call, not for subsequent calls)
    if (!guid) {
        guid = uuidv4();
    }
    // Ensure required headers are set
    // Spread config.headers first, then override with our required headers
    // Filter out internal control header (X-Use-BaseUrl)
    const incomingHeaders = config.headers;
    const { 'X-Use-BaseUrl': _useBaseUrl, ...cleanHeaders } = incomingHeaders || {};
    // Use same headers as trigger-otp for consistency
    const headers = {
        client_scope_identifier: "Web", // Required header for unauth endpoints (lowercase)
        ...cleanHeaders,
        "x-correlation-id": correlationId,
        guid: guid, // Required by unauth endpoints (lowercase to match helpers.ts)
    };
    // Only set Content-Type for requests with a body (POST, PUT, PATCH, DELETE)
    // For GET requests, omit Content-Type to avoid triggering CORS preflight
    const method = (config.method || 'GET').toUpperCase();
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
        headers["Content-Type"] = "application/json";
    }
    // Explicitly ensure Authorization header is set if unAuthToken was found
    if (unAuthToken && !headers.Authorization) {
        // Always use Bearer format for trigger-otp endpoint (to match working cURL)
        headers.Authorization = `Bearer ${unAuthToken}`;
        if (typeof window !== 'undefined' &&
            typeof config.url === 'string' &&
            config.url.includes('/trigger-otp')) {
            console.log('[DEBUG] trigger-otp Authorization header (Bearer):', headers.Authorization);
        }
    }
    // Add x-api-key if available (required header for both regular and T1 endpoints)
    if (xApiKey) {
        headers["x-api-key"] = xApiKey;
    }
    else {
        console.warn("[service.ts] x-api-key is missing. This may cause the API request to fail.", "URL:", config.url);
    }
    // Add Captcha-Token if available
    if (captchaToken) {
        headers["Captcha-Token"] = captchaToken;
    }
    // Add isHeadersPassed query parameter (required for regular baseUrl endpoints)
    // Skip for T1 endpoints (baseUrlt1) as they don't require this parameter
    const params = useBaseUrlt1
        ? config.params
        : {
            ...config.params,
            isHeadersPassed: true,
        };
    // Log request details for debugging (only in development)
    if (typeof window !== "undefined" && window.location.hostname === "localhost") {
        console.log("[service.ts] Making unauth request:", {
            url: `${baseURL}${config.url}`,
            method: config.method || "GET",
            useBaseUrlt1,
            headers: Object.keys(headers),
            hasAuthorization: !!headers.Authorization,
            hasXApiKey: !!headers["x-api-key"],
            hasCaptchaToken: !!headers["Captcha-Token"],
            hasGuid: !!headers.guid,
            params,
        });
    }
    const makeRequest = async () => {
        // Debug log to confirm client_scope_identifier and Authorization are present
        if (typeof window !== "undefined" && window.location.hostname === "localhost") {
            if (typeof config.url === 'string' && (config.url.includes('/trigger-otp') || config.url.includes('/validate-otp'))) {
                console.log(`[DEBUG] Final headers for ${config.url}:`, headers);
            }
        }
        const response = await axios.request({
            ...config,
            baseURL,
            headers,
            params,
            cancelToken: source.token,
            withCredentials: false, // Disable sending cookies to avoid CORS issues
        });
        return ensureUnauthResponseHasNoErrorEnvelope(response);
    };
    const promise = makeRequest().catch(async (error) => {
        // Retry once on 403 AccessDenied (token may have expired between check and request)
        const is403AccessDenied = error.response?.status === 403 &&
            error.response?.headers?.["x-amzn-errortype"] === "AccessDeniedException";
        if (is403AccessDenied &&
            typeof window !== "undefined" &&
            window.__authShellConfig?.refreshUnauthTokenIfNeeded) {
            try {
                // Coordinate with other concurrent 403 retriers: reuse in-flight refresh promise
                // so we do not trigger multiple refresh calls.
                if (!unauthRefreshOn403Promise) {
                    unauthRefreshOn403Promise = window
                        .__authShellConfig.refreshUnauthTokenIfNeeded()
                        .finally(() => {
                        unauthRefreshOn403Promise = null;
                    });
                }
                await unauthRefreshOn403Promise;
                // Token may not be immediately available after refresh (async config update).
                // Poll briefly so legitimate retries are not skipped when refresh succeeded.
                const getToken = () => window.__authShellConfig?.getConfigValue?.("unAuthToken") ?? null;
                let newToken = getToken();
                const delayMs = 50;
                const maxAttempts = 3;
                for (let attempt = 1; attempt < maxAttempts && !newToken; attempt++) {
                    await new Promise((r) => setTimeout(r, delayMs));
                    newToken = getToken();
                }
                if (newToken) {
                    // Use a new headers object for the retry to avoid mutating the shared reference
                    // and prevent race conditions with other concurrent requests.
                    const retryHeaders = { ...headers, Authorization: `Bearer ${newToken}` };
                    return axios
                        .request({
                        ...config,
                        baseURL,
                        headers: retryHeaders,
                        params,
                        cancelToken: source.token,
                        withCredentials: false,
                    })
                        .then((response) => ensureUnauthResponseHasNoErrorEnvelope(response));
                }
                console.warn("[service.ts] Token refresh completed but unAuthToken was not available; retry skipped. URL:", config.url);
            }
            catch (refreshError) {
                // Refresh failed (network/API); rethrow original 403 so caller sees API error
                console.warn("[service.ts] unAuth token refresh on 403 retry failed:", refreshError);
            }
        }
        // Enhanced error logging for debugging
        console.error("[service.ts] API request failed:", {
            url: `${baseURL}${config.url}`,
            method: config.method || "GET",
            error: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            responseData: error.response?.data,
            requestHeaders: headers,
        });
        throw error;
    });
    // @ts-expect-error - adding cancel method to the promise for React Query
    promise.cancel = () => {
        source.cancel("Query was cancelled by React Query");
    };
    return promise;
};
//# sourceMappingURL=service.js.map